import { Stack, useTheme } from '@mui/material';
import { Export, PencilSimple } from '@phosphor-icons/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import VCard from 'vcard-creator';
import { isIOS } from 'react-device-detect';

import {
  Button,
  InternalPage,
  LabelValuePair,
  PageNotFound,
  RemoveButton,
  Spacer,
} from 'theSource/components';
import { ErrorResponseDto, ResponseDto } from 'theSource/types';
import { useApiMutation, useWebShare } from 'theSource/utils';
import { Contacts } from '../types';
import { GET_CONTACTS_QUERY_KEY } from '../utils';
import {
  DELETE_CONTACT_QUERY_KEY,
  getContactFields,
  getSelectedContact,
  mapContactDeleteDto,
} from './utils';
import { ContactDeleteRequestDto } from './types';
import { ROUTES } from 'theSource/app/routes';

export interface MyContactDetailsPageProps {
  data: Contacts;
}

export const MyContactDetailsPage = ({ data }: MyContactDetailsPageProps) => {
  const { contactId } = useParams();
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [files, setFiles] = useState<File[]>([]);

  const { share, canShare } = useWebShare({ files });

  const { isLoading: isDeleting, mutate: removeContact } = useApiMutation<
    ResponseDto,
    ErrorResponseDto,
    ContactDeleteRequestDto
  >({
    queryKey: DELETE_CONTACT_QUERY_KEY,
    queryKeysToInvalidate: GET_CONTACTS_QUERY_KEY,
    method: 'delete',
    endpoint: '/contacts',
    onSuccess: () => navigate(ROUTES.additional.profile.contacts.path),
    getErrorMessage: (error) => error.response?.data?.details,
    showSuccessSnackbar: false,
  });

  const selectedContact = useMemo(
    () => getSelectedContact(data, contactId!),
    [data, contactId]
  )!;
  const contactFields = useMemo(
    () => getContactFields(selectedContact),
    [selectedContact]
  );

  const buildVCardString = useCallback(() => {
    if (!selectedContact) {
      return;
    }

    const vCard = new VCard();
    vCard
      .addName('', selectedContact.name)
      .addRole(selectedContact.title)
      .addEmail(selectedContact.email)
      .addPhoneNumber(selectedContact.homePhone, 'type=HOME')
      .addPhoneNumber(selectedContact.workPhone, 'type=WORK')
      .addPhoneNumber(selectedContact.cellPhone, 'type=CELL')
      .addAddress(selectedContact.address)
      .addURL(selectedContact.url)
      .addNote(selectedContact.notes);

    return vCard.toString();
  }, [selectedContact]);

  useEffect(() => {
    const vCardString = buildVCardString();
    if (!vCardString) {
      return;
    }

    const files = [
      new File([vCardString], `${selectedContact.name}.vcf`, {
        type: 'text/vcard',
      }),
    ];
    setFiles(files);
  }, [buildVCardString, selectedContact]);

  if (!selectedContact) {
    return <PageNotFound />;
  }

  const handleRemove = () =>
    removeContact(mapContactDeleteDto(selectedContact));

  const ShareButton = (
    <Button
      data-testid="share-btn"
      variant="tertiary"
      label={t('common:share')}
      onClick={share}
      endIcon={<Export />}
    />
  );

  const handleEditContact = () => navigate('edit');

  return (
    <InternalPage
      testId="contact-details-page"
      title={selectedContact.name ?? ''}
      isLoading={!selectedContact}
      withBackLink
      CustomActionComponent={isIOS && canShare ? ShareButton : undefined}
    >
      <>
        {selectedContact.isEditable && (
          <>
            <Button
              data-testid="edit-button"
              label={t('common:editInfoButtonLabel')}
              startIcon={<PencilSimple weight="bold" />}
              variant="secondary"
              onClick={handleEditContact}
            />
            <Spacer height={8} />
          </>
        )}
        <Stack spacing={theme.spacing(4)}>
          {contactFields.length > 0 && (
            <>
              {contactFields.map((field) => (
                <LabelValuePair key={field.label} {...field} />
              ))}
              <Spacer height={8} />
            </>
          )}
        </Stack>
        {selectedContact.isEditable && (
          <RemoveButton
            itemName={t('pages.contacts:contact')}
            description={t('messages.contacts:removeConfirmation')}
            onConfirm={handleRemove}
            isLoading={isDeleting}
          />
        )}
      </>
    </InternalPage>
  );
};
